/**
 * O CSS do botão e da caixa, dentro do Shadow DOM.
 *
 * `:host { all: initial }` corta a herança da página; tudo em `px`, sem `rem` (que herdaria o
 * `font-size` do `html` do site). O painel usa `dvh` com reserva em `vh`, e ocupa a tela inteira até
 * 480px de largura, respeitando as áreas seguras do iOS.
 */
export const LAUNCHER_CSS = `
:host { all: initial; }
.wrap { --nk-color: #4f46e5; all: initial; position: fixed; bottom: 20px; right: 20px; z-index: __Z__; display: none;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
.wrap[data-ready="true"] { display: block; }
.wrap[data-position="bottom_left"] { right: auto; left: 20px; }
.launcher { all: initial; box-sizing: border-box; width: 56px; height: 56px; border-radius: 50%; background: var(--nk-color);
  color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer;
  box-shadow: 0 4px 14px rgba(0,0,0,.22); transition: transform .15s ease; }
.launcher:hover { transform: scale(1.05); }
.launcher:focus-visible { outline: 3px solid #111827; outline-offset: 3px; }
.launcher svg { width: 26px; height: 26px; display: block; }
.badge { all: initial; box-sizing: border-box; position: absolute; top: -4px; right: -4px; min-width: 20px; height: 20px; padding: 0 6px;
  border-radius: 10px; background: #dc2626; color: #fff; font: 700 12px/20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
  text-align: center; box-shadow: 0 0 0 2px #fff; }
.badge[hidden] { display: none; }
.wrap[data-position="bottom_left"] .badge { right: auto; left: 40px; }
.panel { all: initial; box-sizing: border-box; position: absolute; bottom: 72px; right: 0; width: 400px; height: 600px;
  max-height: calc(100vh - 120px); max-height: calc(100dvh - 120px); max-width: calc(100vw - 40px);
  border-radius: 16px; overflow: hidden; background: #fff; box-shadow: 0 12px 40px rgba(0,0,0,.25);
  display: none; opacity: 0; transform: translateY(8px); transition: opacity .15s ease, transform .15s ease; }
.wrap[data-position="bottom_left"] .panel { right: auto; left: 0; }
.panel[data-open="true"] { display: block; opacity: 1; transform: none; }
.panel iframe { all: initial; display: block; width: 100%; height: 100%; border: 0; }
@media (max-width: 480px) {
  .panel { position: fixed; inset: 0; width: 100vw; height: 100vh; height: 100dvh; max-width: none; max-height: none; border-radius: 0; bottom: 0;
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left); }
  .wrap:has(.panel[data-open="true"]) .launcher, .wrap:has(.panel[data-open="true"]) .badge { display: none; }
}
/*
 * O balão proativo.
 *
 * Acima do botão e do mesmo lado dele, para não cobrir o conteúdo do site nem o próprio botão. Largura
 * limitada porque o texto é escrito por quem administra o chat e pode vir maior do que caberia; a quebra
 * de palavra evita que uma URL colada estoure a caixa na página de outra pessoa.
 *
 * Valores explícitos em tudo: o Shadow DOM isola o CSS do site, e é o que faz o balão sobreviver a uma
 * folha de estilo hostil.
 */
.teaser {
  position: absolute; bottom: 76px; display: flex; align-items: flex-start; gap: 8px;
  max-width: 280px; padding: 12px 14px; box-sizing: border-box;
  background: #fff; color: #1f2937; border-radius: 14px; border: 1px solid rgba(0,0,0,.08);
  box-shadow: 0 10px 25px rgba(0,0,0,.15); font: 400 14px/1.4 system-ui, -apple-system, "Segoe UI", sans-serif;
  animation: nk-teaser-in .18s ease-out;
}
.wrap[data-position="bottom_right"] .teaser { right: 0; }
.wrap[data-position="bottom_left"] .teaser { left: 0; }
.teaser-text {
  all: unset; cursor: pointer; flex: 1; text-align: left; color: inherit; font: inherit;
  overflow-wrap: anywhere; word-break: break-word;
}
.teaser-text:focus-visible, .teaser-close:focus-visible { outline: 2px solid var(--nk-color, #4f46e5); outline-offset: 2px; border-radius: 4px; }
.teaser-close {
  all: unset; cursor: pointer; color: #9ca3af; font-size: 18px; line-height: 1; padding: 0 2px; flex: none;
}
.teaser-close:hover { color: #4b5563; }
@keyframes nk-teaser-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

/* O destaque do botão: dois pulsos discretos, e não uma animação infinita no canto da tela. */
.launcher[data-highlight="true"] { animation: nk-pulse 1.1s ease-in-out 2; }
@keyframes nk-pulse {
  0%, 100% { box-shadow: 0 4px 14px rgba(0,0,0,.2); }
  50% { box-shadow: 0 4px 14px rgba(0,0,0,.2), 0 0 0 10px color-mix(in srgb, var(--nk-color, #4f46e5) 22%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .launcher, .panel { transition: none; }
  .launcher:hover { transform: none; }
  /*
   * Quem pede menos movimento não recebe movimento.
   *
   * Não é preferência estética: quem liga isso costuma ter motivo vestibular ou de atenção, e uma
   * animação insistente no canto da tela é exatamente o que incomoda. O balão continua aparecendo — só
   * não se mexe para chegar.
   */
  .teaser { animation: none; }
  .launcher[data-highlight="true"] { animation: none; outline: 3px solid var(--nk-color, #4f46e5); outline-offset: 3px; }
}
`;

const svg = (path: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${path}</svg>`;

export const LAUNCHER_ICON_OPEN = svg('<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>');
export const LAUNCHER_ICON_CLOSE = svg('<path d="M18 6 6 18"/><path d="m6 6 12 12"/>');
